import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasShellContext, withIsInShell } from "@insite/client-framework/Components/IsInShell";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps, GridOffset } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import loadCarouselProducts from "@store/Handlers/LoadCarouselProducts";
import setSliderDrawerIsOpen from "@store/Handlers/SetSliderDrawerIsOpen";
import parse from "html-react-parser";
import * as React from "react";
import { ResolveThunks } from "react-redux";
import { css, ThemeProps, withTheme } from "styled-components";

const enum fields {
    title = "title",
    bodyText = "bodyText",
    image = "image",
    imageMobile = "imageMobile",
    layout = "layout",
    imagePosition = "imagePosition",
    imageMobilePosition = "imageMobilePosition",
    minHeight = "minHeight",
    minMobileHeight = "minMobileHeight",
    backgroundColor = "backgroundColor",
    textColor = "textColor",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
        [fields.bodyText]: string;
        [fields.image]: string;
        [fields.imageMobile]: string;
        [fields.minHeight]: number;
        [fields.minMobileHeight]: number;
        [fields.layout]: "imageText" | "textImage";
        [fields.imagePosition]: "cover" | "contain";
        [fields.imageMobilePosition]: "cover" | "contain";
        [fields.backgroundColor]: string;
        [fields.textColor]: string;
    };
}

type Props = OwnProps & ThemeProps<BaseTheme> & HasShellContext;

export interface TextImageStyles {
    wrapper?: InjectableCss;
    textZone?: InjectableCss;
    titleText?: TypographyPresentationProps;
    container?: GridContainerProps;
    imageGrid?: GridItemProps;
    image?: InjectableCss;
    textGrid?: GridItemProps;
}

export const TextImageStyles: TextImageStyles = {
    container: {
        gap: 0,
        css: css`
            overflow: hidden;
            max-width: none !important;
            ${GridOffset} {
                align-items: center;
            }
        `,
    },
    image: {
        css: css`
            width: 100%;
            height: 100%;
            background-position: center;
            background-repeat: no-repeat;
        `,
    },
    imageGrid: {
        width: [12, 12, 6, 6, 6],
        css: css`
            position: relative !important;
            align-self: stretch;
        `,
    },
    textZone: {
        css: css`
            padding: 2rem;
        `,
    },
    textGrid: {
        width: [12, 12, 6, 6, 6],
    },
    titleText: {
        variant: "h2",
        size: 22,
        weight: "bold",
    },
};

const styles = TextImageStyles;

const TextImage: React.FC<Props> = ({ id, fields }) => {
    const wrapperStyles = {
        css: css`
            ${styles.wrapper?.css}
            background-color: ${fields.backgroundColor};
        `,
    };

    const imageGrid = {
        width: styles.imageGrid?.width,
        css: css`
            ${styles.imageGrid?.css}
            ${({ theme }: { theme: BaseTheme }) => {
                return breakpointMediaQueries(
                    theme,
                    [
                        css`
                            order: 1;
                        `,
                        css`
                            order: 1;
                        `,
                        css`
                            order: ${fields.layout === "imageText" ? 1 : 2};
                        `,
                    ],
                    "min",
                );
            }}
        `,
    };

    const image = {
        css: css`
            ${styles.image?.css}
            ${({ theme }: { theme: BaseTheme }) => {
                if (fields.imageMobile !== "") {
                    return breakpointMediaQueries(
                        theme,
                        [
                            css`
                                background-image: url(${fields.imageMobile});
                                background-size: ${fields.imageMobilePosition};
                                min-height: ${fields.minMobileHeight}px;
                            `,
                            null,
                            null,
                            css`
                                background-image: url(${fields.image});
                                background-size: ${fields.imagePosition};
                                min-height: ${fields.minHeight}px;
                            `,
                        ],
                        "min",
                    );
                }
                return `
                    background-image: url(${fields.image});
                    background-size: ${fields.imagePosition};
                    min-height:${fields.minHeight}px;
                `;
            }}
        `,
    };

    const textGrid = {
        width: styles.textGrid?.width,
        css: css`
            ${styles.textGrid?.css || ""}
            color: ${fields.textColor};
            display: block;
            ${({ theme }: { theme: BaseTheme }) => {
                return breakpointMediaQueries(
                    theme,
                    [
                        css`
                            order: 2;
                        `,
                        css`
                            order: 2;
                        `,
                        css`
                            order: ${fields.layout === "imageText" ? 2 : 1};
                        `,
                    ],
                    "min",
                );
            }}
        `,
    };

    return (
        <StyledWrapper {...wrapperStyles}>
            <GridContainer {...styles.container}>
                <GridItem {...imageGrid}>
                    <StyledWrapper {...image}></StyledWrapper>
                </GridItem>
                <GridItem {...textGrid}>
                    <StyledWrapper {...styles.textZone}>
                        <Typography {...styles.titleText}>{fields.title}</Typography>
                        {parse(fields.bodyText, parserOptions)}
                    </StyledWrapper>
                </GridItem>
            </GridContainer>
        </StyledWrapper>
    );
};

const basicTab = {
    displayName: "Basic",
    sortOrder: 1,
};
const imageTab = {
    displayName: "Image",
    sortOrder: 2,
};
const mobileImageTab = {
    displayName: "Mobile Image",
    sortOrder: 3,
};

const widgetModule: WidgetModule = {
    component: withTheme(withIsInShell(TextImage)),
    definition: {
        group: "Common",
        icon: "text",
        fieldDefinitions: [
            {
                fieldType: "General",
                name: fields.backgroundColor,
                editorTemplate: "ColorPickerField",
                displayName: "Background Color",
                defaultValue: "white",
                tab: basicTab,
            },
            {
                fieldType: "General",
                name: fields.textColor,
                editorTemplate: "ColorPickerField",
                displayName: "Text Color",
                defaultValue: "black",
                tab: basicTab,
            },
            {
                name: fields.title,
                displayName: "Title",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
                sortOrder: 0,
                tab: basicTab,
            },
            {
                fieldType: "Translatable",
                name: fields.bodyText,
                editorTemplate: "RichTextField",
                displayName: "Body Text",
                defaultValue: "",
                tab: basicTab,
                extendedConfig: { height: 150 },
                expandedToolbarButtons: {
                    moreMisc: {},
                    code: {},
                },
            },
            {
                fieldType: "General",
                name: fields.minHeight,
                editorTemplate: "IntegerField",
                displayName: "Minimum Height",
                defaultValue: 300,
                tab: imageTab,
            },
            {
                fieldType: "General",
                name: fields.minMobileHeight,
                editorTemplate: "IntegerField",
                displayName: "Minimum Height",
                defaultValue: 200,
                tab: mobileImageTab,
            },
            {
                fieldType: "Translatable",
                name: fields.image,
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                tab: imageTab,
                tooltip: "If mobile image is not used, this image is displayed for all sizes.",
            },
            {
                fieldType: "Translatable",
                name: fields.imageMobile,
                editorTemplate: "ImagePickerField",
                tab: mobileImageTab,
                defaultValue: "",
            },
            {
                name: fields.imagePosition,
                displayName: "Position",
                editorTemplate: "RadioButtonsField",
                defaultValue: "cover",
                fieldType: "General",
                tab: imageTab,
                options: [
                    {
                        displayName: "Cover",
                        value: "cover",
                    },
                    {
                        displayName: "Contain",
                        value: "contain",
                    },
                ],
            },
            {
                name: fields.imageMobilePosition,
                displayName: "Position",
                editorTemplate: "RadioButtonsField",
                defaultValue: "cover",
                fieldType: "General",
                tab: mobileImageTab,
                options: [
                    {
                        displayName: "Cover",
                        value: "cover",
                    },
                    {
                        displayName: "Contain",
                        value: "contain",
                    },
                ],
            },
            {
                name: fields.layout,
                displayName: "Layout",
                editorTemplate: "RadioButtonsField",
                defaultValue: "imageText",
                fieldType: "General",
                tooltip: "Layout only applies to desktop. Image will always be above text on smaller screens.",
                tab: basicTab,
                options: [
                    {
                        displayName: "Image + Text",
                        value: "imageText",
                    },
                    {
                        displayName: "Text + Image",
                        value: "textImage",
                    },
                ],
            },
        ],
    },
};

export default widgetModule;
